import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Shield, 
  Star, 
  TrendingUp,
  Calendar,
  Award,
  AlertCircle,
  Clock,
  Camera,
  Save,
  Eye,
  EyeOff,
  X,
  Settings,
  BarChart,
  CheckCircle
} from 'lucide-react';
import { authAPI, reputationAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useThemeColors } from '../theme/ThemeContext';
import dummyData from '../data/dummyData.json';
import SEO from '../components/SEO';

const Profile = () => {
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSetup, setTwoFactorSetup] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const { user, updateUser, logout } = useAuth();
  const queryClient = useQueryClient();

  // 2FA setup mutation
  const setupTwoFactorMutation = useMutation(authAPI.setupTwoFactor, {
    onSuccess: (response) => {
      setTwoFactorSetup(response.data.data);
      toast.success('2FA setup initiated! Scan the QR code with your authenticator app.');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to setup 2FA');
    }
  });

  // 2FA enable mutation
  const enableTwoFactorMutation = useMutation(authAPI.enableTwoFactor, {
    onSuccess: (response) => {
      setTwoFactorEnabled(true);
      setTwoFactorSetup(null);
      
      // Update user state to reflect 2FA enabled
      if (user) {
        updateUser({
          ...user,
          two_factor_enabled: true
        });
      }
      
      toast.success('2FA enabled successfully! Save your backup codes safely.');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to enable 2FA');
    }
  });

  // 2FA disable mutation
  const disableTwoFactorMutation = useMutation(authAPI.disableTwoFactor, {
    onSuccess: () => {
      setTwoFactorEnabled(false);
      setTwoFactorSetup(null);
      
      // Update user state to reflect 2FA disabled
      if (user) {
        updateUser({
          ...user,
          two_factor_enabled: false
        });
      }
      
      toast.success('2FA disabled successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to disable 2FA');
    }
  });

  const handleSetupTwoFactor = () => {
    setupTwoFactorMutation.mutate();
  };

  const handleEnableTwoFactor = (verificationCode) => {
    enableTwoFactorMutation.mutate(verificationCode);
  };

  const handleDisableTwoFactor = (password) => {
    disableTwoFactorMutation.mutate(password);
  };

  // Initialize 2FA state from user data
  useEffect(() => {
    if (user) {
      setTwoFactorEnabled(user.two_factor_enabled || false);
    }
  }, [user]);

  // Profile form
  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm({
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || '',
    },
  });

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch: watchPassword,
    formState: { errors: passwordErrors },
  } = useForm();

  const newPassword = watchPassword('newPassword');

  // Fetch reputation data
  const {
    data: reputation,
    isLoading: isLoadingReputation,
  } = useQuery(
    'reputation',
    reputationAPI.getReputation,
    {
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data?.data;
        if (apiData) {
          return apiData;
        }
        // Use dummy data as fallback
        return dummyData.dashboard.reputation;
      },
    }
  );

  // Fetch reputation events
  const {
    data: reputationEvents,
  } = useQuery(
    'reputationEvents',
    reputationAPI.getReputationEvents,
    {
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data?.data?.events || [];
        if (apiData.length > 0) {
          return apiData;
        }
        // Use dummy data as fallback
        return dummyData.dashboard.reputation_events;
      },
    }
  );

  // Fetch login history
  const {
    data: loginHistory,
    isLoading: isLoadingLoginHistory,
  } = useQuery(
    'loginHistory',
    () => authAPI.getLoginHistory(10),
    {
      enabled: showLoginHistory,
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data?.data || [];
        if (apiData.length > 0) {
          return apiData;
        }
        // Use dummy data as fallback
        return dummyData.dashboard.login_history;
      },
    }
  );

  // Update profile mutation
  const updateProfileMutation = useMutation(authAPI.updateProfile, {
    onSuccess: (response) => {
      toast.success('Profile updated successfully!');
      updateUser(response.data.data);
      queryClient.invalidateQueries('profile');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    },
  });

  // Update password mutation
  const updatePasswordMutation = useMutation(authAPI.changePassword, {
    onSuccess: () => {
      toast.success('Password changed successfully!');
      setShowPasswordModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to change password');
    },
  });

  const onProfileFormSubmit = (data) => {
    updateProfileMutation.mutate(data);
  };

  const onPasswordFormSubmit = (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    updatePasswordMutation.mutate(data);
  };

  const handleResendVerification = async () => {
    if (!user?.email) return;

    console.log('Current user state before resend:', user);
    setIsSendingVerification(true);
    try {
      const response = await authAPI.resendVerification(user.email);
      console.log('Resend verification response:', response.data);
      if (response.data.success) {
        if (response.data.data.alreadyVerified) {
          toast.success('Your email is already verified!');
          // Update user state to reflect verification status
          console.log('Updating user state with email_verified: true');
          updateUser({ email_verified: true });
          console.log('User state after update call');
        } else {
          toast.success('Verification email sent! Please check your inbox.');
        }
      } else {
        toast.error(response.data.message || 'Failed to send verification email');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send verification email');
    } finally {
      setIsSendingVerification(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const getReputationColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getReputationLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Normal';
    return 'Poor';
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'agreement_completed':
        return <TrendingUp className="h-4 w-4 text-theme-success" />;
      case 'cancellation':
        return <AlertCircle className="h-4 w-4 text-theme-error" />;
      case 'delivery_failure':
        return <X className="h-4 w-4 text-theme-error" />;
      case 'admin_adjustment':
        return <Award className="h-4 w-4 text-theme-info" />;
      default:
        return <Star className="h-4 w-4 text-theme-secondary" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
      <SEO 
        title="Profile" 
        description="Manage your account information, reputation score, and verification status on TripTrot"
        keywords={['user profile', 'account settings', 'reputation score', 'verification status']}
        canonical="/profile"
        noindex={true}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "name": "Profile",
          "description": "User profile management page"
        }}
      />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-theme-primary">Profile</h1>
        <p className="mt-2 text-theme-secondary">Manage your account information and reputation</p>
      </div>

      {/* Profile Overview Card */}
      <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden mb-8">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-theme-primary via-theme-info to-theme-success" />
        
        {/* Content */}
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-4 sm:space-y-0">
            {/* Avatar */}
            <div className="relative sm:flex-shrink-0">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-theme-primary/10 to-theme-primary/5 flex items-center justify-center border border-theme-primary/20">
                <User className="h-10 w-10 text-theme-primary" />
              </div>
              {/* <button className="absolute bottom-0 right-0 p-1.5 bg-theme-primary rounded-full text-white hover:bg-theme-primary-hover transition-colors border-2 border-theme-card">
                <Camera className="h-3 w-3" />
              </button> */}
            </div>

            {/* User Info */}
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl font-bold text-theme-primary">
                {user?.first_name} {user?.last_name}
              </h2>
              <p className="text-theme-secondary">{user?.email}</p>
              <div className="flex flex-col sm:flex-row sm:items-center mt-2 space-y-2 sm:space-y-0 sm:space-x-4">
                <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.verification_status === 'verified' 
                    ? 'bg-theme-success-100 text-theme-success-800 border-theme-success/20'
                    : user?.verification_status === 'partial'
                    ? 'bg-theme-warning-100 text-theme-warning-800 border-theme-warning/20'
                    : 'bg-theme-neutral-100 text-theme-neutral-800 border-theme-light'
                }`}>
                  <Shield className="h-3 w-3 mr-1" />
                  {user?.verification_status || 'unverified'}
                </span>
                <span className="text-sm text-theme-secondary">
                  Member since {new Date(user?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Reputation Summary */}
            {reputation && (
              <div className="text-center sm:flex-shrink-0">
                <div className={`text-3xl font-bold ${getReputationColor(reputation.global_score)}`}>
                  {reputation.global_score}
                </div>
                <div className="text-sm text-theme-secondary">Reputation</div>
                <div className={`text-xs font-medium ${getReputationColor(reputation.global_score)}`}>
                  {getReputationLabel(reputation.global_score)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sleek Tabs */}
      <div className="mb-8">
        {/* Mobile: Vertical tabs | Desktop: Horizontal tabs */}
        <div className="sm:hidden">
          <div className="bg-theme-card border border-theme-light rounded-2xl p-2 space-y-1">
            {[
              { key: 'profile', label: 'Profile', icon: User },
              { key: 'reputation', label: 'Reputation', icon: BarChart },
              { key: 'security', label: 'Security', icon: Shield }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center w-full px-3 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-theme-primary text-white shadow-lg'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-neutral-25'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop version */}
        <div className="hidden sm:block">
          <div className="bg-theme-card border border-theme-light rounded-2xl p-2 flex flex-wrap gap-2">
            {[
              { key: 'profile', label: 'Profile', icon: User },
              { key: 'reputation', label: 'Reputation', icon: BarChart },
              { key: 'security', label: 'Security', icon: Shield }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 flex-1 min-w-0 sm:flex-none ${
                  activeTab === tab.key
                    ? 'bg-theme-primary text-white shadow-lg'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-neutral-25'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Form */}
          <div className="lg:col-span-2">
            <div className="bg-theme-card rounded-lg shadow-md border border-theme-light p-6">
              <h3 className="text-lg font-medium text-theme-primary mb-6">Personal Information</h3>
              
              <form onSubmit={handleProfileSubmit(onProfileFormSubmit)} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-theme-secondary mb-1">
                      First Name
                    </label>
                    <input
                      {...registerProfile('first_name', { required: 'First name is required' })}
                      type="text"
                      className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                    />
                    {profileErrors.first_name && (
                      <p className="mt-1 text-sm text-theme-error">{profileErrors.first_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-theme-secondary mb-1">
                      Last Name
                    </label>
                    <input
                      {...registerProfile('last_name', { required: 'Last name is required' })}
                      type="text"
                      className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                    />
                    {profileErrors.last_name && (
                      <p className="mt-1 text-sm text-theme-error">{profileErrors.last_name.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-3 py-2 border border-theme-light rounded-md bg-theme-neutral-100 text-theme-tertiary"
                  />
                  <p className="mt-1 text-sm text-theme-tertiary">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-secondary mb-1">
                    Phone Number
                  </label>
                  <input
                    {...registerProfile('phone', {
                      pattern: {
                        value: /^[+]?[\d\s-()]+$/,
                        message: 'Invalid phone number',
                      },
                    })}
                    type="tel"
                    className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                    placeholder="+251 911 234 567"
                  />
                  {profileErrors.phone && (
                    <p className="mt-1 text-sm text-theme-error">{profileErrors.phone.message}</p>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={updateProfileMutation.isLoading}
                    className="flex items-center px-4 py-2 bg-theme-primary text-theme-inverse rounded-md hover:bg-theme-primary-hover disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {updateProfileMutation.isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="space-y-6">
            <div className="bg-theme-card rounded-lg shadow-md border border-theme-light p-6">
              <h3 className="text-lg font-medium text-theme-primary mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-theme-secondary">Verification Status</span>
                  <span className={`text-sm font-medium ${
                    user?.verification_status === 'verified'
                      ? 'text-theme-success'
                      : 'text-theme-tertiary'
                  }`}>
                    {user?.verification_status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-theme-secondary">Email Verification</span>
                  <span className={`text-sm font-medium ${
                    user?.email_verified 
                      ? 'text-theme-success'
                      : 'text-theme-tertiary'
                  }`}>
                    {user?.email_verified ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-theme-secondary">Member Since</span>
                  <span className="text-sm font-medium text-theme-primary">
                    {new Date(user?.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-theme-secondary">Account Type</span>
                  <span className="text-sm font-medium text-theme-primary capitalize">
                    {user?.role || 'user'}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-theme-card rounded-lg shadow-md border border-theme-light p-6">
              <h3 className="text-lg font-medium text-theme-primary mb-4">Security</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="w-full flex items-center justify-between p-3 border border-theme-light rounded-md hover:bg-theme-neutral-100"
                >
                  <span className="text-sm text-theme-secondary">Change Password</span>
                  <Shield className="h-4 w-4 text-theme-secondary" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reputation' && (
        <div className="space-y-6">
          {/* Reputation Scores */}
          {reputation && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-theme-card rounded-lg shadow-md border border-theme-light p-6">
                <h3 className="text-lg font-medium text-theme-primary mb-4">Global Reputation</h3>
                <div className="text-center">
                  <div className={`text-4xl font-bold ${getReputationColor(reputation.global_score)}`}>
                    {reputation.global_score}
                  </div>
                  <div className={`text-sm font-medium mt-2 ${getReputationColor(reputation.global_score)}`}>
                    {getReputationLabel(reputation.global_score)}
                  </div>
                </div>
              </div>

              <div className="bg-theme-card rounded-lg shadow-md border border-theme-light p-6">
                <h3 className="text-lg font-medium text-theme-primary mb-4">Sender Reputation</h3>
                <div className="text-center">
                  <div className={`text-4xl font-bold ${getReputationColor(reputation.sender_score)}`}>
                    {reputation.sender_score}
                  </div>
                  <div className={`text-sm font-medium mt-2 ${getReputationColor(reputation.sender_score)}`}>
                    {getReputationLabel(reputation.sender_score)}
                  </div>
                </div>
              </div>

              <div className="bg-theme-card rounded-lg shadow-md border border-theme-light p-6">
                <h3 className="text-lg font-medium text-theme-primary mb-4">Traveller Reputation</h3>
                <div className="text-center">
                  <div className={`text-4xl font-bold ${getReputationColor(reputation.traveller_score)}`}>
                    {reputation.traveller_score}
                  </div>
                  <div className={`text-sm font-medium mt-2 ${getReputationColor(reputation.traveller_score)}`}>
                    {getReputationLabel(reputation.traveller_score)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reputation History */}
          <div className="bg-theme-card rounded-lg shadow-md border border-theme-light">
            <div className="p-6 border-b border-theme-light">
              <h3 className="text-lg font-medium text-theme-primary">Reputation History</h3>
            </div>
            <div className="divide-y divide-theme-light">
              {(Array.isArray(reputationEvents) ? reputationEvents : []).map((event) => (
                <div key={event.id} className="p-4 hover:bg-theme-neutral-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {getEventIcon(event.event_type)}
                      <div className="ml-3">
                        <p className="text-sm font-medium text-theme-primary capitalize">
                          {event.event_type ? event.event_type.replace('_', ' ') : 'Unknown Event'}
                        </p>
                        <p className="text-sm text-theme-tertiary">{event.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        event.score_change > 0 ? 'text-theme-success' : 'text-theme-error'
                      }`}>
                        {event.score_change > 0 ? '+' : ''}{event.score_change}
                      </p>
                      <p className="text-xs text-theme-tertiary">
                        {new Date(event.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {(Array.isArray(reputationEvents) ? reputationEvents : []).length === 0 && (
                <div className="p-6 text-center text-theme-tertiary">
                  No reputation events yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="bg-theme-card rounded-lg shadow-md border border-theme-light p-6">
            <h3 className="text-lg font-medium text-theme-primary mb-6">Security Settings</h3>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 border border-theme-light rounded-md">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-theme-primary">Email Verification</h4>
                  <p className="text-sm text-theme-tertiary">
                    {user?.email_verified ? 'Your email is verified and secure' : 'Your email is not verified'}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  {user?.email_verified ? (
                    <Shield className="h-5 w-5 text-theme-success" />
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-theme-warning" />
                      <button
                        onClick={handleResendVerification}
                        disabled={isSendingVerification}
                        className="px-3 py-1 text-sm border border-theme-light rounded-md hover:bg-theme-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSendingVerification ? 'Sending...' : 'Verify Email'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border border-theme-light rounded-md">
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-theme-primary">Identity Verification</h4>
                  <p className="text-sm text-theme-tertiary">
                    {user?.verification_status === 'verified' 
                      ? 'Your identity has been verified' 
                      : user?.verification_status === 'partial' 
                      ? 'Your identity verification is pending review'
                      : 'Complete identity verification to unlock all features'
                    }
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  {user?.verification_status === 'verified' ? (
                    <Shield className="h-5 w-5 text-theme-success" />
                  ) : user?.verification_status === 'pending' ? (
                    <>
                      <Clock className="h-5 w-5 text-theme-warning" />
                      <button
                        onClick={() => navigate('/verification')}
                        className="px-3 py-1 text-sm border border-theme-light rounded-md hover:bg-theme-neutral-100"
                      >
                        Check Status
                      </button>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-theme-warning" />
                      <button
                        onClick={() => navigate('/verification')}
                        className="px-3 py-1 text-sm border border-theme-light rounded-md hover:bg-theme-neutral-100"
                      >
                        Verify Identity
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 border border-theme-light rounded-md">
                <div>
                  <h4 className="text-sm font-medium text-theme-primary">Two-Factor Authentication</h4>
                  <p className="text-sm text-theme-tertiary">Add an extra layer of security</p>
                </div>
                <button 
                  onClick={() => setShow2FAModal(true)}
                  className="px-3 py-1 text-sm border border-theme-light rounded-md hover:bg-theme-neutral-100"
                >
                  {twoFactorEnabled ? 'Manage' : 'Enable'}
                </button>
              </div>

              <div className="flex items-center justify-between p-4 border border-theme-light rounded-md">
                <div>
                  <h4 className="text-sm font-medium text-theme-primary">Password</h4>
                  <p className="text-sm text-theme-tertiary">Last changed {user?.password_changed_at ? new Date(user.password_changed_at).toLocaleDateString() : 'Never'}</p>
                </div>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="px-3 py-1 text-sm border border-theme-light rounded-md hover:bg-theme-neutral-100"
                >
                  Change Password
                </button>
              </div>

              <div className="flex items-center justify-between p-4 border border-theme-light rounded-md">
                <div>
                  <h4 className="text-sm font-medium text-theme-primary">Login History</h4>
                  <p className="text-sm text-theme-tertiary">View your recent login activity</p>
                </div>
                <button 
                  onClick={() => setShowLoginHistory(true)}
                  className="px-3 py-1 text-sm border border-theme-light rounded-md hover:bg-theme-neutral-100"
                >
                  View
                </button>
              </div>

              <div className="flex items-center justify-between p-4 border border-red-200 rounded-md bg-red-50">
                <div>
                  <h4 className="text-sm font-medium text-red-900">Sign Out</h4>
                  <p className="text-sm text-red-600">Sign out of your account</p>
                </div>
                <button 
                  onClick={() => setShowLogoutModal(true)}
                  className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded-md hover:bg-red-100"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-theme-light">
              <h2 className="text-xl font-semibold text-theme-primary">Change Password</h2>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-theme-tertiary hover:text-theme-secondary"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit(onPasswordFormSubmit)} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    {...registerPassword('currentPassword', { required: 'Current password is required' })}
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="w-full px-3 py-2 pr-10 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4 text-theme-secondary" />
                    ) : (
                      <Eye className="h-4 w-4 text-theme-secondary" />
                    )}
                  </button>
                </div>
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-sm text-theme-error">{passwordErrors.currentPassword.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  New Password
                </label>
                <div className="relative">
                  <input
                    {...registerPassword('newPassword', {
                      required: 'New password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    })}
                    type={showNewPassword ? 'text' : 'password'}
                    className="w-full px-3 py-2 pr-10 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-theme-secondary" />
                    ) : (
                      <Eye className="h-4 w-4 text-theme-secondary" />
                    )}
                  </button>
                </div>
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-secondary mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    {...registerPassword('confirmPassword', { required: 'Please confirm your password' })}
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="w-full px-3 py-2 pr-10 border border-theme-light rounded-md focus:ring-theme-primary focus:border-theme-primary"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2.5"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-theme-secondary" />
                    ) : (
                      <Eye className="h-4 w-4 text-theme-secondary" />
                    )}
                  </button>
                </div>
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t border-theme-light">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 text-theme-secondary border border-theme-light rounded-md hover:bg-theme-neutral-100"
                  disabled={updatePasswordMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-theme-primary text-theme-inverse rounded-md hover:bg-theme-primary-hover disabled:opacity-50"
                  disabled={updatePasswordMutation.isLoading}
                >
                  {updatePasswordMutation.isLoading ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Login History Modal */}
      {showLoginHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-theme-light">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-theme-primary">Login History</h3>
                <button
                  onClick={() => setShowLoginHistory(false)}
                  className="text-theme-tertiary hover:text-theme-secondary"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {isLoadingLoginHistory ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary mx-auto"></div>
                  <p className="mt-2 text-sm text-theme-secondary">Loading login history...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {loginHistory.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="mx-auto h-12 w-12 text-theme-secondary" />
                      <h3 className="mt-2 text-sm font-medium text-theme-primary">No login history</h3>
                      <p className="mt-1 text-sm text-theme-tertiary">Your first login will appear here</p>
                    </div>
                  ) : (
                    loginHistory.map((session) => (
                      <div key={session.id} className="flex items-center justify-between p-3 border border-theme-light rounded-md">
                        <div className="flex items-center space-x-3">
                          <div className={`w-2 h-2 rounded-full ${
                            session.is_current_session ? 'bg-theme-success' : 'bg-theme-tertiary'
                          }`}></div>
                          <div>
                            <p className="text-sm font-medium text-theme-primary">
                              {session.ip_address}
                            </p>
                            <p className="text-sm text-theme-tertiary">
                              {new Date(session.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            session.is_current_session 
                              ? 'bg-theme-success-100 text-theme-success-800'
                              : 'bg-theme-neutral-100 text-theme-neutral-800'
                          }`}>
                            {session.is_current_session ? 'Current' : 'Previous'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              
              <div className="mt-6 p-4 bg-theme-neutral-100 rounded-md">
                <p className="text-sm text-theme-secondary">
                  <strong>Security Tip:</strong> If you don't recognize any of these logins, please change your password immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-theme-error-100 rounded-full mb-4">
                <X className="h-6 w-6 text-theme-error" />
              </div>
              <h3 className="text-lg font-medium text-theme-primary text-center mb-2">
                Sign Out of Your Account?
              </h3>
              <p className="text-sm text-theme-tertiary text-center mb-6">
                Are you sure you want to sign out? You'll need to sign in again to access your account.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 px-4 py-2 border border-theme-light text-theme-secondary rounded-md hover:bg-theme-neutral-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setShowLogoutModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-theme-error text-theme-inverse rounded-md hover:bg-theme-error-hover"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-theme-primary bg-opacity-10 rounded-full mb-4">
                <Shield className="h-6 w-6 text-theme-primary" />
              </div>
              <h3 className="text-lg font-medium text-theme-primary text-center mb-2">
                Two-Factor Authentication
              </h3>
              <p className="text-sm text-theme-tertiary text-center mb-6">
                {twoFactorEnabled 
                  ? 'Manage your 2FA settings below' 
                  : 'Enable two-factor authentication to add an extra layer of security to your account'}
              </p>
              
              <div className="space-y-4">
                {twoFactorSetup ? (
                  // Setup flow
                  <div className="space-y-4">
                    <div className="text-center">
                      <img src={twoFactorSetup.qrCode} alt="QR Code" className="mx-auto mb-4" />
                      <p className="text-sm text-theme-secondary mb-2">
                        Scan this QR code with your authenticator app
                      </p>
                      <div className="bg-theme-neutral-100 p-3 rounded-md">
                        <p className="text-xs font-mono text-theme-secondary break-all">
                          {twoFactorSetup.manualEntryKey}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={verificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                            setVerificationCode(value);
                          }}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-theme-primary focus:border-theme-primary text-center text-lg tracking-widest ${
                            verificationCode.length === 6 
                              ? 'border-theme-success ring-2 ring-theme-success ring-opacity-50' 
                              : 'border-theme-light'
                          }`}
                          maxLength={6}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                        />
                        {verificationCode.length === 6 && (
                          <div className="absolute inset-y-0 right-3 flex items-center">
                            <CheckCircle className="h-5 w-5 text-theme-success" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-theme-secondary mt-2">
                        Enter the 6-digit code from your authenticator app (Google Authenticator, Authy, etc.)
                      </p>
                    </div>
                    
                    <button
                      onClick={() => handleEnableTwoFactor(verificationCode)}
                      disabled={enableTwoFactorMutation.isLoading || verificationCode.length !== 6}
                      className="w-full px-4 py-2 bg-theme-success text-theme-inverse rounded-md hover:bg-theme-success-hover disabled:opacity-50"
                    >
                      {enableTwoFactorMutation.isLoading ? 'Enabling...' : 'Enable 2FA'}
                    </button>
                  </div>
                ) : (
                  // Status/Disable flow
                  <div className="space-y-4">
                    <div className="flex items-center justify-center p-4 border border-theme-light rounded-md">
                      {/* <div>
                        <h4 className="text-sm font-medium text-theme-primary">Status</h4>
                        <p className="text-sm text-theme-tertiary">
                          {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                        </p>
                      </div> */}
                      {twoFactorEnabled ? (
                        <div className="space-y-3">
                          <div className="border border-theme-light rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="text-sm font-medium text-theme-primary">Disable 2FA</h4>
                                <p className="text-xs text-theme-secondary mt-1">
                                  Enter your password to remove two-factor authentication
                                </p>
                              </div>
                              <Shield className="h-5 w-5 text-theme-tertiary" />
                            </div>
                            <div className="space-y-3">
                              <input
                                type="password"
                                placeholder="Enter password"
                                value={disablePassword}
                                onChange={(e) => setDisablePassword(e.target.value)}
                                className="w-full px-3 py-2 border border-theme-light rounded-md focus:outline-none focus:ring-1 focus:ring-theme-primary focus:border-theme-primary text-sm placeholder-theme-tertiary"
                              />
                              <button
                                onClick={() => handleDisableTwoFactor(disablePassword)}
                                disabled={disableTwoFactorMutation.isLoading || !disablePassword}
                                className="w-full px-4 py-2 border border-theme-error text-theme-error rounded-md hover:bg-theme-error hover:bg-opacity-5 disabled:opacity-50 text-sm font-medium transition-all duration-200"
                              >
                                {disableTwoFactorMutation.isLoading ? (
                                  <span className="flex items-center justify-center">
                                    <div className="h-4 w-4 border-2 border-theme-error border-t-transparent rounded-full animate-spin mr-2" />
                                    Disabling...
                                  </span>
                                ) : (
                                  'Disable 2FA'
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={handleSetupTwoFactor}
                          disabled={setupTwoFactorMutation.isLoading}
                          className="px-3 py-1 text-sm border border-theme-success text-theme-success rounded-md hover:bg-theme-success hover:bg-opacity-10 disabled:opacity-50"
                        >
                          {setupTwoFactorMutation.isLoading ? 'Setting up...' : 'Enable'}
                        </button>
                      )}
                    </div>
                    
                    {twoFactorEnabled && (
                      <div className="bg-theme-neutral-100 p-3 rounded-md">
                        <p className="text-xs text-theme-secondary">
                          <strong>Note:</strong> Keep your backup codes safe. They can be used if you lose access to your authenticator app.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={() => {
                    setShow2FAModal(false);
                    setTwoFactorSetup(null);
                    setVerificationCode('');
                    setDisablePassword('');
                  }}
                  className="w-full px-4 py-2 border border-theme-light text-theme-secondary rounded-md hover:bg-theme-neutral-100"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
